import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, useMemo } from 'react';

import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getDefaultBidderCreditCardId } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { EventContext } from '@/redux/modules/analytics';
import {
    getCatalogIsInstantAutopay,
    getCatalogPaymentProvider,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getItemSummaryCatalogOnly } from '@/redux/modules/item/summary/itemSummary.selectors';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { getAddressID } from '@/redux/modules/modal.selectors';
import { submitRegisterForCatalog } from '@/redux/modules/registerForCatalog';
import { AddressModalData, AddressModalTitleKey } from '@/types/address/address.types';
import { RegisteredMessage } from '@/types/RegistrationLink';
import { useAppFormatMessage, type TranslationBlock } from '@/utils/intl/useAppFormatMessage';

export type NewItemCardRegisterLinkProps = {
    catalogId: number;
    className?: string;
    itemId?: number;
    primaryButton?: boolean;
    registerToBidMessageId?: string;
};

type RegistrationType = {
    cardId: number;
    catalogId: number;
    itemId: number;
};

const NewItemCardRegisterLinkComponent = ({
    catalogId,
    className,
    itemId,
    primaryButton,
    registerToBidMessageId = 'register_for_auction',
}: NewItemCardRegisterLinkProps) => {
    const formatMessage = useAppFormatMessage();
    const isMobile = useIsMobileLayout();
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const addressId = useAppSelector(getAddressID);
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const cardId = useAppSelector((state) => getDefaultBidderCreditCardId(state, paymentProvider));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryCatalogOnly(state, itemId));

    const dispatch = useAppDispatch();

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: itemId || catalogId,
            itemId,
            type: itemId
                ? CompleteAccountModalContext.RegisterForAuctionItem
                : CompleteAccountModalContext.RegisterForAuction,
        }),
        [itemId, catalogId]
    );

    const { approved, declined, pending, registered } = useAppSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );

    const addressData: AddressModalData = {
        addressID: addressId,
        titleKey: AddressModalTitleKey.RegisterForAuction,
    };
    const registerForCatalog = ({ cardId, catalogId, itemId }: RegistrationType) => {
        dispatch(submitRegisterForCatalog({ cardId, catalogId, itemId }));
    };
    const openRegisterModal = ({ catalogId, itemId }: RegistrationType) => {
        dispatch(openRegisterForAuctionModal({ catalogId, isInstantAutopay, itemId }));
    };

    if (isCatalogOnly) {
        if (registered || pending) {
            return <BodySecondary color="secondary">{formatMessage(['onlineAuctionRequested'])}</BodySecondary>;
        }
        return (
            <ShouldLogInContainer<{
                cardId: number;
                catalogId: number;
                itemId: number;
            }>
                completeAddressData={addressData}
                requireCompleteProfile
                requireLogin
                requireVerifiedAddress
                runFunction={registerForCatalog}
                runFunctionArgument={{ cardId, catalogId, itemId }}
            >
                <SubtleLink className={cn(className, 'no-underline hover:underline')}>
                    {formatMessage(['requestOnlineAuction'])}
                </SubtleLink>
            </ShouldLogInContainer>
        );
    }

    if (declined) {
        return (
            <BodySecondary
                className={cn('inline-block select-none print:hidden', className)}
                color={isMobile ? 'primary' : 'secondary'}
            >
                {formatMessage([
                    'registration_status_label',
                    isMobile ? 'registration_declined' : 'registration_denied',
                ])}
            </BodySecondary>
        );
    }
    if (!registered) {
        return (
            <ShouldLogInContainer<{ catalogId: number; itemId: number }>
                completeAddressData={addressData}
                modalContext={modalContext}
                requireCompleteProfile
                requireLogin
                requireVerifiedAddress
                runFunction={openRegisterModal}
                runFunctionArgument={{ catalogId, itemId }}
            >
                <Button
                    className={className}
                    size="sm"
                    variant={primaryButton ? 'filled' : 'outlined'}
                >
                    {formatMessage([registerToBidMessageId as keyof TranslationBlock])}
                </Button>
            </ShouldLogInContainer>
        );
    }

    if (pending) {
        return (
            <BodySecondary color="secondary">{formatMessage([RegisteredMessage.RegistrationPending])}</BodySecondary>
        );
    }

    if (approved || registered) {
        return null;
    }

    return (
        <ShouldLogInContainer<{ catalogId: number; itemId: number }>
            completeAddressData={addressData}
            modalContext={modalContext}
            requireCompleteProfile
            requireLogin
            requireVerifiedAddress
            runFunction={openRegisterModal}
            runFunctionArgument={{ catalogId, itemId }}
        >
            <InlineButton className={className}>
                {formatMessage([registerToBidMessageId as keyof TranslationBlock])}
            </InlineButton>
        </ShouldLogInContainer>
    );
};

export const NewItemCardRegisterLink = memo<NewItemCardRegisterLinkProps>(NewItemCardRegisterLinkComponent);
